import React, { useContext, useEffect, useRef, useState } from "react";
import { Drawer } from "@mui/material";
import { MatrixClient } from "matrix-js-sdk";
import styled from "styled-components";
import {
    cancelMeetingOccurrence,
    cancelMeetingOccurrenceAndFollowing,
    getMeetingOccurrence,
    listMeetings,
    MeetingListItem,
    MeetingOccurrenceAccess,
    rescheduleMeetingOccurrence,
} from "src/api/meetingsApi";
import { ChatContext } from "src/scenes/Chat/ChatContext";
import { useMeetingCallNotes } from "src/scenes/Meet/useMeetingCallNotes";

type MeetingSection = "scheduled" | "past";
type MeetingsBySection = Record<MeetingSection, MeetingListItem[]>;

const StyledDrawer = styled(Drawer)`
    .MuiDrawer-paper {
        width: min(30rem, 100vw);
        box-sizing: border-box;
        border-left: 1px solid var(--calendar-border, #d8deec);
        background: var(--calendar-surface, #fff);
        box-shadow: -12px 0 32px rgba(15, 23, 42, .16);
    }
`;

const DrawerBody = styled.div`
    height: 100%;
    box-sizing: border-box;
    overflow: auto;
    background: var(--calendar-surface, #fff);
    color: var(--calendar-text, #171a22);
    font-family: Manrope, Helvetica, sans-serif;
`;

const Header = styled.header`
    position: sticky;
    top: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    min-height: 4.5rem;
    padding: 0 1.25rem;
    border-bottom: 1px solid var(--calendar-border, #d8deec);
    background: var(--calendar-surface, #fff);
`;

const Title = styled.h2`
    margin: 0;
    font-size: 1.5rem;
    line-height: 1.2;
`;

const IconButton = styled.button`
    width: 2rem;
    height: 2rem;
    border: 0;
    border-radius: .4rem;
    background: transparent;
    color: inherit;
    cursor: pointer;
    font-size: 1.25rem;

    &:hover { background: var(--calendar-surface-muted, #eef2f7); }
`;

const Content = styled.div`
    display: grid;
    gap: 1rem;
    padding: 1.25rem;
`;

const Tabs = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: .25rem;
    padding: .25rem;
    border-radius: .5rem;
    background: var(--calendar-surface-muted, #eef2f7);
`;

const Tab = styled.button<{ $active: boolean }>`
    min-height: 2.5rem;
    border: 0;
    border-radius: .35rem;
    background: ${p => p.$active ? "var(--calendar-surface, #fff)" : "transparent"};
    color: ${p => p.$active ? "var(--calendar-text, #171a22)" : "var(--calendar-text-muted, #6a7387)"};
    cursor: pointer;
    font: 800 .78rem/1 Manrope, Helvetica, sans-serif;
    box-shadow: ${p => p.$active ? "0 1px 4px rgba(15, 23, 42, .12)" : "none"};
`;

const MeetingSectionBlock = styled.section`
    display: grid;
    gap: .75rem;
`;

const SectionHeading = styled.h3`
    margin: 0;
    color: var(--calendar-text-muted, #6a7387);
    font: 800 .78rem/1 Manrope, Helvetica, sans-serif;
    text-transform: uppercase;
`;

const MeetingList = styled.div`
    display: grid;
    gap: .75rem;
`;

const MeetingRow = styled.article`
    padding: 1rem;
    border: 1px solid var(--calendar-border, #d8deec);
    border-radius: .5rem;
    background: var(--calendar-surface, #fff);
    box-shadow: 0 2px 8px rgba(15, 23, 42, .05);
`;

const MeetingTitle = styled.h3`
    margin: 0 0 .35rem;
    font-size: 1.05rem;
`;

const MeetingMeta = styled.p`
    margin: .15rem 0;
    color: var(--calendar-text-muted, #6a7387);
    font-size: .78rem;
    line-height: 1.45;
`;

const Status = styled.span<{ $ready?: boolean; $active?: boolean }>`
    display: inline-flex;
    width: fit-content;
    align-items: center;
    min-height: 1.75rem;
    margin-top: .45rem;
    padding: 0 .65rem;
    border-radius: 999px;
    background: ${p => p.$active ? "#e1f8ea" : p.$ready ? "#e8f6ed" : "var(--calendar-surface-muted, #eef2f7)"};
    color: ${p => p.$active || p.$ready ? "#08783f" : "var(--calendar-text-muted, #6a7387)"};
    font: 800 .72rem/1 Manrope, Helvetica, sans-serif;
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: .5rem;
    margin-top: .75rem;
`;

const Action = styled.button<{ $primary?: boolean }>`
    min-height: 2rem;
    padding: 0 .75rem;
    border: 1px solid ${p => p.$primary ? "transparent" : "var(--calendar-border, #d8deec)"};
    border-radius: .4rem;
    background: ${p => p.$primary ? "#4058d6" : "transparent"};
    color: ${p => p.$primary ? "#fff" : "inherit"};
    cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
    opacity: ${p => p.disabled ? .55 : 1};
    font: 800 .72rem/1 Manrope, Helvetica, sans-serif;
`;

const PrimaryAction = styled(Action)`
    width: 100%;
    min-height: 2.75rem;
    justify-content: center;
    font-size: .82rem;
`;

const Notice = styled.p`
    padding: 1rem;
    margin: 0;
    border-radius: .5rem;
    background: var(--calendar-surface-muted, #eef2f7);
    color: var(--calendar-text-muted, #6a7387);
    font-size: .82rem;
    line-height: 1.45;
`;

const SectionNotice = styled(Notice)`
    border: 1px dashed var(--calendar-border, #d8deec);
    background: transparent;
`;

const Notes = styled.div`
    max-height: 13rem;
    margin-top: .75rem;
    padding: .75rem;
    overflow: auto;
    border: 1px solid var(--calendar-border, #d8deec);
    border-radius: .4rem;
    background: var(--calendar-surface-muted, #f7f9fc);
`;

const ScheduleEditor = styled.div`
    display: grid;
    gap: .5rem;
    margin-top: .75rem;
    padding: .75rem;
    border: 1px solid var(--calendar-border, #d8deec);
    border-radius: .4rem;
`;

const ScheduleInput = styled.input`
    width: 100%;
    min-height: 2.25rem;
    box-sizing: border-box;
    padding: 0 .6rem;
    border: 1px solid var(--calendar-border, #d8deec);
    border-radius: .35rem;
    background: var(--calendar-surface, #fff);
    color: inherit;
    font: 600 .78rem/1 Manrope, Helvetica, sans-serif;
`;

const Note = styled.div`
    padding: .55rem 0;
    border-bottom: 1px solid var(--calendar-border, #d8deec);
    font-size: .8rem;
    line-height: 1.45;

    &:last-child { border-bottom: 0; }
`;

const formatSchedule = (meeting: MeetingListItem): string => {
    const start = meeting.occurrence.scheduledStart || meeting.scheduledStart;
    if (!start) return "Started from Chat";
    return new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: meeting.timezone,
    }).format(new Date(start));
};

const recurrenceLabels: Record<string, string> = {
    everyDay: "Every day",
    everyWeek: "Every week",
    everyMonth: "Every month",
    everyYear: "Every year",
};

const formatMeetingSchedule = (meeting: MeetingListItem): string => {
    const recurrence = recurrenceLabels[meeting.repeat];
    return recurrence ? `${formatSchedule(meeting)} · ${recurrence}` : formatSchedule(meeting);
};

const meetingPath = (meeting: MeetingListItem): string => (
    `/meet/${encodeURIComponent(meeting.meetingToken)}?occurrence=${encodeURIComponent(meeting.occurrence.occurrenceUuid)}`
);

const portalMeetingPath = (meeting: MeetingListItem): string => `${meetingPath(meeting)}&source=portal`;

const meetingLocalDateTime = (value: string | null, timezone: string): string => {
    if (!value) return "";
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    }).formatToParts(new Date(value));
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(item => item.type === type)?.value || "";
    return `${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}`;
};

function HistoricalNotesContent({ matrixClient, roomId }: { matrixClient: MatrixClient; roomId: string }) {
    const { notes } = useMeetingCallNotes(matrixClient, roomId);

    if (!matrixClient.getRoom(roomId)) {
        return <Notes><MeetingMeta>Encrypted notes are still syncing to this device.</MeetingMeta></Notes>;
    }
    return (
        <Notes>
            {notes.length === 0
                ? <MeetingMeta>No notes were added to this meeting.</MeetingMeta>
                : notes.map(note => (
                    <Note key={note.id}>
                        <strong>{note.authorName}</strong><br />
                        {note.body}
                    </Note>
                ))}
        </Notes>
    );
}

function HistoricalNotes({ roomId }: { roomId: string }) {
    const { matrixClient } = useContext(ChatContext);
    if (!matrixClient) {
        return <Notes><MeetingMeta>Preparing encrypted meeting notes…</MeetingMeta></Notes>;
    }
    return <HistoricalNotesContent matrixClient={matrixClient} roomId={roomId} />;
}

export default function MeetingsDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
    const [section, setSection] = useState<MeetingSection>("scheduled");
    const [meetings, setMeetings] = useState<MeetingsBySection>({ scheduled: [], past: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [copiedOccurrenceUuid, setCopiedOccurrenceUuid] = useState<string | null>(null);
    const copyFeedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [notesAccess, setNotesAccess] = useState<Record<string, MeetingOccurrenceAccess | null>>({});
    const [scheduleDraft, setScheduleDraft] = useState<{
        occurrenceUuid: string;
        start: string;
        end: string;
        following: boolean;
    } | null>(null);

    useEffect(() => {
        if (!open) return undefined;
        let disposed = false;
        setLoading(true);
        setError("");
        listMeetings(section)
            .then(result => {
                if (!disposed) {
                    setMeetings(previous => ({ ...previous, [section]: result.meetings }));
                }
            })
            .catch(() => {
                if (!disposed) setError("We could not load your meetings. Please try again.");
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });
        return () => { disposed = true; };
    }, [open, section]);

    useEffect(() => () => {
        if (copyFeedbackTimer.current) clearTimeout(copyFeedbackTimer.current);
    }, []);

    const copyMeetingLink = async (meeting: MeetingListItem): Promise<void> => {
        try {
            await navigator.clipboard.writeText(`${window.location.origin}${meetingPath(meeting)}`);
            setCopiedOccurrenceUuid(meeting.occurrence.occurrenceUuid);
            if (copyFeedbackTimer.current) clearTimeout(copyFeedbackTimer.current);
            copyFeedbackTimer.current = setTimeout(() => setCopiedOccurrenceUuid(null), 2000);
        } catch {
            setError("We could not copy that meeting link.");
        }
    };

    const toggleNotes = async (meeting: MeetingListItem): Promise<void> => {
        const occurrenceUuid = meeting.occurrence.occurrenceUuid;
        if (Object.prototype.hasOwnProperty.call(notesAccess, occurrenceUuid)) {
            setNotesAccess(previous => {
                const next = { ...previous };
                delete next[occurrenceUuid];
                return next;
            });
            return;
        }
        try {
            const access = await getMeetingOccurrence(meeting.meetingUuid, occurrenceUuid);
            setNotesAccess(previous => ({ ...previous, [occurrenceUuid]: access }));
        } catch {
            setError("We could not load the notes for that meeting.");
        }
    };

    const cancelOccurrence = async (meeting: MeetingListItem): Promise<void> => {
        if (!window.confirm(`Cancel this occurrence of ${meeting.title}?`)) return;
        try {
            await cancelMeetingOccurrence(meeting.meetingUuid, meeting.occurrence.occurrenceUuid);
            setMeetings(previous => ({
                ...previous,
                scheduled: previous.scheduled.filter(item => (
                    item.occurrence.occurrenceUuid !== meeting.occurrence.occurrenceUuid
                )),
            }));
        } catch {
            setError("We could not cancel that meeting occurrence.");
        }
    };

    const cancelFollowing = async (meeting: MeetingListItem): Promise<void> => {
        if (!window.confirm(`Cancel this and every following occurrence of ${meeting.title}?`)) return;
        try {
            await cancelMeetingOccurrenceAndFollowing(
                meeting.meetingUuid,
                meeting.occurrence.occurrenceUuid
            );
            setMeetings(previous => ({
                ...previous,
                scheduled: previous.scheduled.filter(item => item.meetingUuid !== meeting.meetingUuid),
            }));
        } catch {
            setError("We could not cancel this and the following meeting occurrences.");
        }
    };

    const editSchedule = (meeting: MeetingListItem): void => {
        setScheduleDraft({
            occurrenceUuid: meeting.occurrence.occurrenceUuid,
            start: meetingLocalDateTime(meeting.occurrence.scheduledStart, meeting.timezone),
            end: meetingLocalDateTime(meeting.occurrence.scheduledEnd, meeting.timezone),
            following: false,
        });
    };

    const saveSchedule = async (meeting: MeetingListItem): Promise<void> => {
        if (!scheduleDraft?.start || !scheduleDraft.end) return;
        setError("");
        try {
            const updated = await rescheduleMeetingOccurrence(
                meeting.meetingUuid,
                meeting.occurrence.occurrenceUuid,
                scheduleDraft.start,
                scheduleDraft.end,
                scheduleDraft.following
            );
            setMeetings(previous => ({
                ...previous,
                scheduled: previous.scheduled.map(item => (
                    item.occurrence.occurrenceUuid === meeting.occurrence.occurrenceUuid
                        ? { ...item, ...updated }
                        : item
                )),
            }));
            setScheduleDraft(null);
        } catch {
            setError("We could not reschedule that meeting occurrence.");
        }
    };

    const renderMeeting = (meeting: MeetingListItem, section: MeetingSection) => {
        const occurrenceUuid = meeting.occurrence.occurrenceUuid;
        const notes = notesAccess[occurrenceUuid];
        const isHost = meeting.canManage;
        const isActive = meeting.occurrence.status === "active"
            && meeting.connectedParticipantCount > 0;
        const actionLabel = isActive
            ? isHost ? "Go to Meeting" : "Join Meeting"
            : meeting.canStart ? "Start Meeting" : isHost ? "Go to Meeting" : "Waiting for Host";
        const statusLabel = isActive
            ? "Meeting in progress"
            : meeting.canStart ? "Ready to start" : isHost ? "Scheduled" : "Waiting for host";

        return (
            <MeetingRow key={occurrenceUuid}>
                <MeetingTitle>{meeting.title}</MeetingTitle>
                <MeetingMeta>{formatMeetingSchedule(meeting)}</MeetingMeta>
                <MeetingMeta>Hosted by {meeting.hostName}</MeetingMeta>
                {section === "scheduled" && (
                    <Status $active={isActive} $ready={meeting.canStart}>{statusLabel}</Status>
                )}
                <Actions>
                    {section === "scheduled" && (
                        <PrimaryAction
                            type="button"
                            $primary={isActive || Boolean(meeting.canStart) || isHost}
                            disabled={!isActive && !meeting.canStart && !isHost}
                            onClick={() => window.open(
                                portalMeetingPath(meeting),
                                `meeting-${occurrenceUuid}`,
                                "popup,width=1280,height=800"
                            )}
                        >
                            {actionLabel}
                        </PrimaryAction>
                    )}
                    {section === "past" && meeting.canReadNotes && (
                        <Action type="button" onClick={() => toggleNotes(meeting)}>
                            {notes ? "Hide Notes" : "View Meeting Notes →"}
                        </Action>
                    )}
                    {meeting.canShareLink && (
                        <Action
                            type="button"
                            aria-live="polite"
                            onClick={() => copyMeetingLink(meeting)}
                        >
                            {copiedOccurrenceUuid === occurrenceUuid ? "Copied" : "Copy Link"}
                        </Action>
                    )}
                    {section === "scheduled" && meeting.canManage && !isActive && (
                        <Action type="button" onClick={() => editSchedule(meeting)}>
                            Reschedule
                        </Action>
                    )}
                    {section === "scheduled" && meeting.canManage && !isActive && (
                        <Action type="button" onClick={() => cancelOccurrence(meeting)}>
                            Cancel Occurrence
                        </Action>
                    )}
                    {section === "scheduled"
                        && meeting.canManage
                        && !isActive
                        && meeting.repeat !== "none" && (
                        <Action type="button" onClick={() => cancelFollowing(meeting)}>
                            Cancel This and Following
                        </Action>
                    )}
                </Actions>
                {scheduleDraft?.occurrenceUuid === occurrenceUuid && (
                    <ScheduleEditor>
                        <label>
                            <MeetingMeta as="span">Start ({meeting.timezone})</MeetingMeta>
                            <ScheduleInput
                                type="datetime-local"
                                value={scheduleDraft.start}
                                onChange={({ target: { value } }) => setScheduleDraft(previous => (
                                    previous ? { ...previous, start: value } : previous
                                ))}
                            />
                        </label>
                        <label>
                            <MeetingMeta as="span">End ({meeting.timezone})</MeetingMeta>
                            <ScheduleInput
                                type="datetime-local"
                                value={scheduleDraft.end}
                                onChange={({ target: { value } }) => setScheduleDraft(previous => (
                                    previous ? { ...previous, end: value } : previous
                                ))}
                            />
                        </label>
                        {meeting.repeat !== "none" && (
                            <label>
                                <input
                                    type="checkbox"
                                    checked={scheduleDraft.following}
                                    onChange={({ target: { checked } }) => setScheduleDraft(previous => (
                                        previous ? { ...previous, following: checked } : previous
                                    ))}
                                /> Apply to this and following occurrences
                            </label>
                        )}
                        <Actions>
                            <Action type="button" $primary onClick={() => saveSchedule(meeting)}>
                                Save Schedule
                            </Action>
                            <Action type="button" onClick={() => setScheduleDraft(null)}>Cancel</Action>
                        </Actions>
                    </ScheduleEditor>
                )}
                {notes?.notesRoomId && <HistoricalNotes roomId={notes.notesRoomId} />}
                {notes && !notes.notesRoomId && <Notice>Notes are not available for this occurrence.</Notice>}
            </MeetingRow>
        );
    };

    const unoccupiedMeetings = meetings.scheduled.filter(meeting => (
        meeting.occurrence.status === "active" && (meeting.connectedParticipantCount ?? 0) === 0
    ));
    const displayedMeetings = section === "scheduled"
        ? meetings.scheduled.filter(meeting => !unoccupiedMeetings.includes(meeting))
        : [
            ...unoccupiedMeetings,
            ...meetings.past.filter(meeting => !unoccupiedMeetings.some(unoccupied => (
                unoccupied.occurrence.occurrenceUuid === meeting.occurrence.occurrenceUuid
            ))),
        ];

    return (
        <StyledDrawer anchor="right" open={open} onClose={onClose}>
            <DrawerBody role="dialog" aria-label="Meetings">
                <Header>
                    <Title>Meetings</Title>
                    <IconButton type="button" aria-label="Close meetings" onClick={onClose}>×</IconButton>
                </Header>
                <Content>
                    <Tabs role="tablist" aria-label="Meeting sections">
                        <Tab
                            type="button"
                            role="tab"
                            aria-selected={section === "scheduled"}
                            $active={section === "scheduled"}
                            onClick={() => setSection("scheduled")}
                        >
                            Scheduled
                        </Tab>
                        <Tab
                            type="button"
                            role="tab"
                            aria-selected={section === "past"}
                            $active={section === "past"}
                            onClick={() => setSection("past")}
                        >
                            Past Meetings
                        </Tab>
                    </Tabs>
                    {loading && <Notice>Loading meetings…</Notice>}
                    {error && <Notice role="alert">{error}</Notice>}
                    {!loading && !error && (
                        <MeetingSectionBlock aria-labelledby={`${section}-meetings-heading`}>
                            <SectionHeading id={`${section}-meetings-heading`}>
                                {section === "scheduled" ? "Scheduled" : "Past Meetings"}
                            </SectionHeading>
                            {displayedMeetings.length === 0
                                ? <SectionNotice>You have no {section} meetings.</SectionNotice>
                                : <MeetingList>{displayedMeetings.map(meeting => renderMeeting(meeting, section))}</MeetingList>}
                        </MeetingSectionBlock>
                    )}
                </Content>
            </DrawerBody>
        </StyledDrawer>
    );
}
